<template>
  <NavFbochasComponent v-if="showPublicNav" />
  <main :class="mainClass">
    <router-view v-slot="{ Component }">
      <AdminShell v-if="showAdminShell">
        <Transition name="admin-view" mode="out-in">
          <component :is="Component" :key="route.fullPath" />
        </Transition>
      </AdminShell>
      <component :is="Component" v-else />
    </router-view>
  </main>
  <FooterFbochasComponent v-if="showPublicNav" />
  <ScrollToTopButton v-if="showPublicNav" />
  <ConfirmDialog ref="confirmDialogRef" />
  <Transition name="network-loading">
    <div
      v-if="showGlobalLoading && !isNetworkRetrying && showAdminShell"
      class="fixed left-1/2 top-[calc(0.75rem+var(--safe-top))] z-[90] flex -translate-x-1/2 items-center gap-2 rounded-full border border-sky-300/20 bg-[#0a1424]/90 px-4 py-2 text-sm font-semibold text-sky-100 shadow-2xl backdrop-blur-xl"
      role="status"
      aria-live="polite"
    >
      <span
        class="h-4 w-4 animate-spin rounded-full border-2 border-sky-200/25 border-t-sky-200"
        aria-hidden="true"
      ></span>
      Cargando…
    </div>
  </Transition>
  <Transition name="page-progress">
    <div
      v-if="showGlobalLoading && !isNetworkRetrying && !showAdminShell"
      class="fixed inset-x-0 top-0 z-[90] h-[3px] overflow-hidden bg-white/10"
      role="progressbar"
      aria-label="Cargando contenido"
    >
      <span class="public-loading-bar block h-full w-1/3 rounded-full"></span>
    </div>
  </Transition>
  <Transition name="network-loading">
    <div
      v-if="isNetworkRetrying"
      class="fixed left-1/2 top-1/2 z-[80] flex w-fit -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-white/10 bg-[#0a0e1a]/90 px-4 py-2 text-sm font-semibold text-white shadow-2xl backdrop-blur-xl"
      role="status"
      aria-live="polite"
    >
      <span
        class="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white"
        aria-hidden="true"
      ></span>
      Reconectando...
    </div>
  </Transition>
</template>

<script setup>
import { computed, onBeforeUnmount, provide, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { isRouteNavigating } from "@/router";
import NavFbochasComponent from "@/components/Home/NavFbochasComponent.vue";
import FooterFbochasComponent from "@/components/Home/FooterFbochasComponent.vue";
import ScrollToTopButton from "@/components/Common/ScrollToTopButton.vue";
import AdminShell from "@/components/Admin/AdminShell.vue";
import ConfirmDialog from "@/components/Common/ConfirmDialog.vue";
import { isNetworkPending, isNetworkRetrying } from "@/services/http/api";

const route = useRoute();
const confirmDialogRef = ref(null);
const showGlobalLoading = ref(false);
let loadingTimer = null;

const isAppLoading = computed(
  () => isRouteNavigating.value || isNetworkPending.value,
);

watch(isAppLoading, (loading) => {
  clearTimeout(loadingTimer);
  if (!loading) {
    showGlobalLoading.value = false;
    return;
  }
  loadingTimer = setTimeout(() => {
    showGlobalLoading.value = true;
  }, 180);
});

onBeforeUnmount(() => clearTimeout(loadingTimer));

// Función global para usar el confirm dialog desde cualquier componente
function $confirm(options) {
  return confirmDialogRef.value?.open(options);
}
provide("$confirm", $confirm);

const isHome = computed(() => route.name === "home");

// El nav y footer públicos se ocultan SOLO en rutas del admin (que tienen su propio layout).
// En cualquier otra ruta se muestran, esté o no logueado.
const showPublicNav = computed(() => {
  return !route.path.startsWith("/admin") && !route.path.startsWith("/login");
});

const showAdminShell = computed(() => {
  return (
    route.path.startsWith("/admin") &&
    route.name !== "admin-setup" &&
    route.name !== "admin-cambiar-password"
  );
});

const mainClass = computed(() => {
  if (isHome.value) return "";
  if (showPublicNav.value) return "pt-[calc(5rem+var(--safe-top))]";
  return "standalone-mobile-top-space";
});
</script>

<style>
/* Admin y login no necesitan reservar espacio para el navbar público.
   La separación superior se conserva únicamente al ejecutar la PWA instalada
   en un dispositivo móvil, dónde también se suma el safe area del sistema. */
.standalone-mobile-top-space {
  padding-top: 0;
}

@media (display-mode: standalone) and (max-width: 767px) {
  .standalone-mobile-top-space {
    padding-top: calc(5rem + var(--safe-top));
  }
}

.admin-view-enter-active,
.admin-view-leave-active {
  transition: opacity 180ms ease, transform 180ms ease, filter 180ms ease;
}

.admin-view-enter-from {
  opacity: 0;
  transform: translateY(8px) scale(0.992);
  filter: blur(2px);
}

.admin-view-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.996);
  filter: blur(1px);
}

@media (prefers-reduced-motion: reduce) {
  .admin-view-enter-active,
  .admin-view-leave-active {
    transition: none;
  }

  .admin-view-enter-from,
  .admin-view-leave-to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}

.network-loading-enter-active,
.network-loading-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.network-loading-enter-from,
.network-loading-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% - 8px));
}

.page-progress-enter-active,
.page-progress-leave-active {
  transition: opacity 140ms ease;
}

.page-progress-enter-from,
.page-progress-leave-to {
  opacity: 0;
}

.public-loading-bar {
  background: linear-gradient(90deg, #38bdf8, #ffffff, #a52121);
  box-shadow: 0 0 12px rgba(56, 189, 248, 0.65);
  animation: public-loading-progress 900ms ease-in-out infinite;
}

@keyframes public-loading-progress {
  from {
    transform: translateX(-110%);
  }
  to {
    transform: translateX(310%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .public-loading-bar {
    animation-duration: 1.8s;
  }
}
</style>
